import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRightLeft, RefreshCw, Download, AlertTriangle, History,
  Database,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import api from '../../lib/axios';
import { useTenant } from '../../contexts/TenantContext';
import { formatDate } from '@/lib/utils';

const fmtDate = (iso) => (iso ? formatDate(iso) || '—' : '—');

export default function FamilyMoveHistoryPage() {
  const { isSuperAdmin } = useTenant();
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(365);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/families/audit/move-history', { params: { days } });
      setItems(res.data.items || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Historie konnte nicht geladen werden.';
      setError(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm('Backfill scannt family.notes nach Migrations-Markern und legt fehlende Audit-Einträge an. Fortfahren?')) return;
    try {
      const res = await api.post('/families/audit/move-history-backfill');
      const r = res.data;
      toast.success(`Backfill: ${r.created} erstellt, ${r.skipped} übersprungen, ${r.scanned} gescannt`);
      load();
    } catch (err) {
      toast.error('Backfill fehlgeschlagen');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const downloadCsv = () => {
    if (items.length === 0) {
      toast.info('Keine Daten zum Export');
      return;
    }
    const header = ['Datum', 'Familie', 'Von', 'Nach', 'Schüler', 'Schüler-Namen', 'Guardians (move)', 'Guardians (clone)', 'SEPA-Mandate', 'Beendete Belegungen', 'Operator', 'Notizen'];
    const rows = items.map((r) => [
      fmtDate(r.moved_at),
      r.family_name,
      r.from_tenant_slug,
      r.to_tenant_slug,
      r.student_count,
      (r.student_names || []).join(' / '),
      r.guardian_moved_count,
      r.guardian_cloned_count,
      r.sepa_mandates_carried,
      r.ended_enrollments_count,
      r.moved_by || '—',
      r.notes || '',
    ]);
    const csv = [header, ...rows].map((row) =>
      row.map((cell) => {
        const s = String(cell ?? '').replace(/"/g, '""');
        return /[;"\n]/.test(s) ? `"${s}"` : s;
      }).join(';'),
    ).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mandanten-umzuege_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('CSV heruntergeladen');
  };

  const filteredItems = items.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'in') return r.to_tenant_slug && r.from_tenant_slug;
    if (filter === 'with-mandate') return r.sepa_mandates_carried > 0;
    if (filter === 'with-clone') return r.guardian_cloned_count > 0;
    return true;
  });

  // Stats
  const stats = items.reduce((acc, r) => {
    const key = `${r.from_tenant_slug} → ${r.to_tenant_slug}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="move-history-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <History className="w-7 h-7 text-brand-red" /> Mandanten-Umzüge (Audit-Log)
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Alle Familienmigrationen mit SEPA-Mandat-Übernahme · für DSGVO + Trägergesellschaft
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white"
              data-testid="history-days-select">
              <option value={30}>letzte 30 Tage</option>
              <option value={90}>letzte 90 Tage</option>
              <option value={365}>letzte 12 Monate</option>
              <option value={730}>letzte 24 Monate</option>
              <option value={3650}>10 Jahre</option>
            </select>
            <Button onClick={load} variant="outline" disabled={loading} data-testid="history-reload-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Laden
            </Button>
            <Button onClick={downloadCsv} disabled={items.length === 0} data-testid="history-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            {isSuperAdmin && (
              <Button onClick={runBackfill} variant="outline"
                className="text-amber-700 border-amber-300 hover:bg-brand-red/5"
                data-testid="history-backfill-btn">
                <Database className="w-4 h-4 mr-2" />
                Backfill aus Notes
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-sm text-rose-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Migrationen total</p>
              <p className="text-2xl font-bold text-slate-900 mt-2">{items.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">SEPA-Mandate übergeben</p>
              <p className="text-2xl font-bold text-emerald-700 mt-2">
                {items.reduce((a, r) => a + (r.sepa_mandates_carried || 0), 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Top-Routen</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(stats).slice(0, 4).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-xs">
                    {k} · {v}
                  </Badge>
                ))}
                {Object.keys(stats).length === 0 && <span className="text-sm text-slate-400">—</span>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="text-slate-500">Filter:</span>
          {[
            { k: 'all', label: 'Alle' },
            { k: 'with-mandate', label: 'Mit SEPA-Mandat' },
            { k: 'with-clone', label: 'Mit Guardian-Klon' },
          ].map(({ k, label }) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 rounded-full border text-xs font-medium transition ${
                filter === k
                  ? 'bg-brand-red text-white border-brand-red'
                  : 'bg-white border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`history-filter-${k}`}>
              {label} ({k === 'all' ? items.length : items.filter((r) => {
                if (k === 'with-mandate') return r.sepa_mandates_carried > 0;
                if (k === 'with-clone') return r.guardian_cloned_count > 0;
                return true;
              }).length})
            </button>
          ))}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">Datum</th>
                  <th className="px-4 py-3 font-semibold">Familie</th>
                  <th className="px-4 py-3 font-semibold">Migration</th>
                  <th className="px-4 py-3 font-semibold text-right">Schüler</th>
                  <th className="px-4 py-3 font-semibold text-right">Guardians</th>
                  <th className="px-4 py-3 font-semibold text-right">SEPA</th>
                  <th className="px-4 py-3 font-semibold text-right">Beendet</th>
                  <th className="px-4 py-3 font-semibold">Notiz</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Lade …</td></tr>
                )}
                {!loading && filteredItems.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Keine Migrationen im Zeitraum
                  </td></tr>
                )}
                {filteredItems.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`history-row-${r.id}`}>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtDate(r.moved_at)}</td>
                    <td className="px-4 py-3 font-medium">
                      {r.family_name}
                      {r.student_names && r.student_names.length > 0 && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.student_names.join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs bg-brand-red/5 border border-brand-red/20 text-brand-red px-2 py-1 rounded font-mono">
                        {r.from_tenant_slug}
                        <ArrowRightLeft className="w-3 h-3" />
                        {r.to_tenant_slug}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.student_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.guardian_moved_count}
                      {r.guardian_cloned_count > 0 && (
                        <span className="ml-1 text-amber-700 text-xs">+{r.guardian_cloned_count} 🧬</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.sepa_mandates_carried > 0
                        ? <span className="text-emerald-700 font-medium">{r.sepa_mandates_carried}</span>
                        : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                      {r.ended_enrollments_count}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center">
          Audit-Quelle: <code>family_move_logs</code> · Backfill nutzt Marker in <code>family.notes</code>
        </p>
      </div>
    </Layout>
  );
}
